import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Modal from "@mui/material/Modal";
import Typography from "@mui/material/Typography";
import {DataGridPro, GridColDef} from "@mui/x-data-grid-pro";
import React, {useEffect, useState} from "react";
import {Organization, Role, User} from "../../../types/userTypes";
import {APP_PERMISSION, defaultUser, EntityType, userColumns} from "../../../constants/userConstants";
import {
    createUser,
    deleteUser,
    getOrganizations,
    getRoles,
    getUsers,
    passwordChange,
    updateUser
} from "../../../queries/userQueries";
import UserForm from "./UserForm";
import {useSnackbar} from "notistack";
import {OP_BUTTON_STYLE} from "../../../constants/styles";
import {IconButton, Stack} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlined from "@ant-design/icons/DeleteOutlined";
import LockResetOutlinedIcon from '@mui/icons-material/LockResetOutlined';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import PermissionRendering from "@/pages/custom-components/UserComponents/PermissionRendering";
import { circularIconHoverSx } from "@/styles/iconHover";

interface UserTableProps {
    updateEntity: EntityType.users | EntityType.organizations | "";
    handleUpdateEntity: (value: EntityType.users | EntityType.organizations | "") => void;
}

const UserTable: React.FC<UserTableProps> = ({ updateEntity, handleUpdateEntity }) => {
    const { enqueueSnackbar } = useSnackbar();

    const [users, setUsers] = useState<User[]>([]);
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false);
    const [roles, setRoles] = useState<Role[]>([]);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<User>(JSON.parse(JSON.stringify(defaultUser)));
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create' | 'update' | 'passwordChange'>('create');
    const [tableColumns, setTableColumns] = useState<GridColDef<User>[]>([]);
    const [openDialogDeleteUser, setOpenDialogDeleteUSer] = useState<boolean>(false);

    async function loadRoles() {
        try {
            setLoadingRoles(true);
            const roles = await getRoles();
            setRoles(roles);
            setLoadingRoles(false);
        } catch (e) {
            console.error(e);
            setLoadingRoles(false);
            enqueueSnackbar("Problems retrieving roles");
        }
    }

    async function loadOrganizations() {
        try {
            const organizations: Organization[] = await getOrganizations();
            setOrganizations(organizations);
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Problems retrieving organizations");
        }
    }

    async function loadUsers() {
        try {
            setLoadingUsers(true);
            const users = await getUsers();
            setUsers(users);
            setLoadingUsers(false);
        } catch (e) {
            console.error(e);
            setLoadingUsers(false);
            enqueueSnackbar("Problems retrieving users");
        }
    }

    useEffect(() => {
        loadUsers();
        loadRoles();
        addActionColumns();
        loadOrganizations();
    }, []);

    useEffect(() => {
        if (updateEntity === EntityType.organizations || updateEntity=== EntityType.users) {
            loadUsers();
            loadRoles();
            addActionColumns();
            loadOrganizations();
            handleUpdateEntity("");
        }
    }, [updateEntity]);

    function onCreateClick() {
        setIsFormOpen(true);
        setFormMode("create");
    }

    async function onUpdateClick(user: User) {
        setSelectedUser(user);
        setIsFormOpen(true);
        setFormMode("update");
    }

    async function onPasswordChangeClick(user: User) {
        setSelectedUser(user);
        setIsFormOpen(true);
        setFormMode("passwordChange");
    }

    async function handleDeleteUser() {
        try {
            await deleteUser(selectedUser.id);
            setUsers(prevState => prevState.filter(user => user.id !== selectedUser.id));
            enqueueSnackbar("User Deleted Successfully");
            setSelectedUser(JSON.parse(JSON.stringify(defaultUser)))
            setOpenDialogDeleteUSer(false);
        } catch (e) {
            console.error(e);
            enqueueSnackbar("User Delete Failed");
        }
        finally {
            handleUpdateEntity(EntityType.organizations);
        }
    }

    function onDeleteClick(user: User) {
        setSelectedUser(user);
        setOpenDialogDeleteUSer(true);
    }

    function handleClose() {
        setIsFormOpen(false);
        setSelectedUser(JSON.parse(JSON.stringify(defaultUser)));
        setFormMode("create");
    }

    function handleUserInfoChange(field: keyof User, value: string) {
        setSelectedUser(prevState => ({ ...prevState, [field]: value }));
    }

    function handleUserRolesChange(roles: Role[]) {
        setSelectedUser(prevState => ({ ...prevState, roles: roles }));
    }

    function handleUserOrganizationsChange(organizations: Organization[]) {
        setSelectedUser(prevState => ({ ...prevState, organizations: organizations }));
        handleUpdateEntity(EntityType.organizations)
    }

    async function handleFormSubmit() {
        try {
            if (formMode === 'create') {
                const tempSelectedUser = { ...selectedUser };
                const createdUserId: { id: string } = await createUser(selectedUser);
                tempSelectedUser.id = createdUserId.id;
                tempSelectedUser.password = "";
                setUsers(prevState => [...prevState, JSON.parse(JSON.stringify(tempSelectedUser))]);
            } else if (formMode === 'update') {
                await updateUser(selectedUser, selectedUser.id);
                const tempUsers = [...users];
                const updatedUserIndex = tempUsers.findIndex(user => user.id === selectedUser.id);
                if (updatedUserIndex > -1) {
                    tempUsers[updatedUserIndex] = selectedUser;
                    setUsers(tempUsers);
                }
            } else {
                await passwordChange(selectedUser.id, selectedUser.password!);
                const tempUsers = [...users];
                const updatedUserIndex = tempUsers.findIndex(user => user.id === selectedUser.id);
                if (updatedUserIndex > -1) {
                    tempUsers[updatedUserIndex] = selectedUser;
                    setUsers(tempUsers);
                }
            }
            handleUpdateEntity(EntityType.users);
            handleClose();
            enqueueSnackbar(`User ${formMode === 'create' ? 'created' : formMode === 'passwordChange' ? 'password changed' : 'updated'}  successfully`);
        } catch (e: any) {
            if (e.response && e.response.data && e.response.data.detail) {
                console.error(e);
                enqueueSnackbar(`Error ${formMode === 'create' ? 'creating' : 'updating'} user: ${e.response.data.detail.message}`);
            } else {
                console.error(e);
                enqueueSnackbar(`Error ${formMode === 'create' ? 'creating' : 'updating'}  user`);
            }
        }
    }

    function addActionColumns() {
        const tempColumns = [...userColumns];
        tempColumns.push(
            {
                field: "actions",
                headerName: "",
                flex: 1,
                renderCell: (params) => (
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        spacing={1}
                        sx={{ width: '100%', height: '100%' }}
                    >

                        <PermissionRendering permissions={[APP_PERMISSION.UPDATE_USER]}>
                            <IconButton
                                data-tour={"users_data_grid_pro_edit_icon"}
                                onClick={(e) => onUpdateClick(params.row)}
                                sx={circularIconHoverSx()}
                            >
                                <EditOutlinedIcon />
                            </IconButton>
                            <IconButton
                                data-tour={"users_datagrid_pro_change_password"}
                                onClick={(e) => onPasswordChangeClick(params.row)}
                                sx={circularIconHoverSx()}
                            >
                                <LockResetOutlinedIcon />
                            </IconButton>
                        </PermissionRendering>

                        <PermissionRendering permissions={[APP_PERMISSION.DELETE_USER]}>
                            <IconButton
                                data-tour={"users_datagrid_pro_delete"}
                                onClick={(e) => onDeleteClick(params.row)}
                                sx={circularIconHoverSx()}
                            >
                                <DeleteOutlined />
                            </IconButton>
                        </PermissionRendering>

                    </Stack>
                ),
            },
        );
        setTableColumns(tempColumns);
    }

    return (
        <Box sx={{marginTop: 3}}>
            <Modal
                open={openDialogDeleteUser}
                onClose={() => setOpenDialogDeleteUSer(false)}
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 1,
                    minWidth: 300
                }}>
                    <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                        Delete User
                    </Typography>
                    <Typography sx={{ mb: 3 }}>
                        {`Are you sure you want to delete ${selectedUser.email}?`}
                    </Typography>
                    <Box display="flex" justifyContent="flex-end" gap={2}>
                        <Button variant="contained" onClick={() => setOpenDialogDeleteUSer(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={() => handleDeleteUser()}>
                            Confirm
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Dialog
                open={isFormOpen}
                onClose={handleClose}
                fullWidth={false}
                sx={{ '& .MuiDialog-paper': { width: '65vw', maxWidth: 'none' } }}
            >
                <DialogTitle>
                    {`${formMode === 'create' ? 'Create User' : formMode === 'passwordChange' ? 'Password Change' : 'Update User'}`}
                </DialogTitle>
                <DialogContent>
                    <UserForm
                        onInfoChange={handleUserInfoChange}
                        onRolesChange={handleUserRolesChange}
                        organizations={organizations}
                        onOrganizationsChange={handleUserOrganizationsChange}
                        user={selectedUser}
                        roles={roles}
                        mode={formMode}
                        sendFormLabel={`${formMode === 'create' ? 'Create User' : formMode === 'passwordChange' ? 'Change' : 'Save'}`}
                        onSubmit={handleFormSubmit}
                        onCloseClick = {handleClose}
                    />
                </DialogContent>
            </Dialog>

            <PermissionRendering permissions={[APP_PERMISSION.CREATE_USER]}>
                <Button data-tour={"users_create_user_button"} startIcon={<AddCircleOutlineOutlinedIcon/>} sx={OP_BUTTON_STYLE} variant="contained" onClick={onCreateClick}>
                    Create User
                </Button>
            </PermissionRendering>


            <DataGridPro
                data-tour={"users_data_grid_pro_total"}
                rows={users}
                columns={tableColumns}
                getRowId={(row) => row.id}
                loading={loadingUsers}
                pageSizeOptions={[5, 25, 50]}
                columnVisibilityModel={{
                    id: false
                }}
                sx={{
                    marginTop: 1
                }}
            />
        </Box>
    );
};

export default UserTable;